import { ReactElement, useEffect, useState } from 'react';
import { Formik } from 'formik';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import queryString from 'query-string';
import { ResetPasswordSchema } from '../../../validators/AuthenticationShema';
import {
  Box,
  CircularProgress,
  Grid,
  Link,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import {
  AppFormikPasswordField,
  AppPrimaryButton,
} from 'shared/components/AppComponents';
import { Utils } from 'shared/helpers';
import { AuthenticationService } from 'services';

const ResetPasswordForm = (): ReactElement => {
  const { uniqueId } = useParams();
  const resetPasswordForm = {
    password: '',
    confirmPassword: '',
  };
  const [isLoading, setIsLoading] = useState(true);
  const [tokenError, setTokenError] = useState('');
  const theme = useTheme();
  const match = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = queryString.parse(location.search);

  // API Calls
  const validateToken = (id: string): void => {
    setIsLoading(true);
    AuthenticationService.validateToken(id)
      .then()
      .catch((error: any) => {
        Utils.handleErrorResponse(error);
        setTokenError(error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const resetPassword = (formValues: any, callback: any): any => {
    const { password } = formValues;
    AuthenticationService.resetPassword(uniqueId!, password)
      .then((response) => {
        console.log('test', response);
        callback(true, response);
      })
      .catch((error) => {
        console.log('test', error);
        callback(false);
        Utils.handleErrorResponse(error);
      });
  };

  // UI Actions
  const actionOnFormSumbit = (formValues: any, formik: any): any => {
    resetPassword(
      { ...formValues, token: queryParams.verificationToken },
      (status: any, response: any) => {
        formik.setSubmitting(false);
        if (status) {
          Utils.handleSuccessResponse(response);
          navigate('/login', { replace: true });
        }
      }
    );
  };

  // Hooks
  useEffect(() => {
    if (uniqueId) {
      validateToken(`${uniqueId}`);
    }
  }, [uniqueId]);

  // UI Elements
  return (
    <>
      {isLoading && (
        <Box className="center-content flex-column">
          <CircularProgress />
          <Typography variant="h2" className="mt-10 poppins-bold">
            Validating...
          </Typography>
        </Box>
      )}
      {!isLoading && tokenError && (
        <Box className="center-content flex-column">
          <Typography variant="h2" className="mt-10">
            {tokenError}
          </Typography>
        </Box>
      )}
      {!isLoading && !tokenError && (
        <Box className="reset-password-form-wrapper ">
          <Typography className="title" variant="h2">
            Reset your password
          </Typography>
          <Typography className="subtitle" variant="body1">
            Type your new password to login
          </Typography>

          <Box mt={7}>
            <Formik
              enableReinitialize
              initialValues={resetPasswordForm}
              validationSchema={ResetPasswordSchema}
              validateOnMount
              onSubmit={(formValues, formik) => {
                actionOnFormSumbit(formValues, formik);
              }}
            >
              {({ handleSubmit, isSubmitting, isValid }) => (
                <form autoComplete="off">
                  <Box mt={2}>
                    <AppFormikPasswordField
                      outerLabel
                      label="Password *"
                      name="password"
                      isCommonCopy
                    />
                  </Box>
                  <Box mt={2}>
                    <AppFormikPasswordField
                      outerLabel
                      label="Confirm Password *"
                      name="confirmPassword"
                      isCommonCopy
                    />
                  </Box>

                  <Box pt={9}>
                    <Grid
                      container
                      spacing={1}
                      direction={match ? 'column-reverse' : 'row'}
                    >
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={6}
                        className="start-content"
                      >
                        <Typography className="subtitle" variant="body1">
                          Are you having problem?
                          <Link
                            className="subtitle-link"
                            href="https://quixxi.com/clarity-ai/"
                          >
                            Contact Admin
                          </Link>
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={6} className="end-content">
                        <AppPrimaryButton
                          fullWidth
                          title="Reset Password"
                          isLoading={isSubmitting}
                          loadingSize={22}
                          testid="button-reset"
                          loadingText="RESETTING"
                          disabled={isSubmitting || !isValid}
                          onClick={handleSubmit}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </form>
              )}
            </Formik>
          </Box>
        </Box>
      )}
    </>
  );
};

export default ResetPasswordForm;
